import React from 'react';
import { Box, Card, Typography, List, ListItem, ListItemAvatar, ListItemText, Avatar, Button, CircularProgress } from '@mui/material';
import { EmptyState } from '@components/DataTable/components/EmptyState';
import { Link } from 'react-router-dom';

interface Company {
    _id: string;
    commercialName: string;
    bossFirstname: string;
    bossLastname: string;
    createdDate: string;
    brandLogo?: { url?: string };
}

interface NewCompaniesProps {
    companies: Company[];
    loading?: boolean;
    onViewAll?: () => void;
}

export const NewCompanies: React.FC<NewCompaniesProps> = ({ companies, loading, onViewAll }) => {
    const formatDate = (date: string) => {
        const d = new Date(date);
        const now = new Date();
        const diff = now.getTime() - d.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) return 'Inscrite aujourd\'hui';
        if (days === 1) return 'Inscrite il y a 1 jour';
        return `Inscrite il y a ${days} jours`;
    };

    return (
        <Card
            sx={{
                borderRadius: '16px',
                p: 2.5,
                height: '100%',
                bgcolor: theme => theme.palette.mode === 'dark' ? '#1a1a2e' : '#fff',
                border: '1px solid',
                borderColor: theme => theme.palette.mode === 'dark' ? '#2d2d44' : '#e0e0e0',
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Nouvelles Entreprises</Typography>
                <Button
                    onClick={onViewAll}
                    variant="text"
                    color="secondary"
                    sx={{py: 0, px: 0.5, borderRadius: '4px', lineHeight: 1}}
                    endIcon={<i className="fi fi-br-angle-small-right" style={{ fontSize: '16px' }} />}>
                    Voir tout
                </Button>
            </Box>

            <List sx={{ p: 0, maxHeight: '400px', overflow: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : companies.length > 0 ? (
                    companies.map((company) => (
                        <ListItem
                            key={company._id}
                            component={Link}
                            to={`/companies/${company._id}/edit`}
                            sx={{
                                px: 0,
                                py: 1.5,
                                borderBottom: '1px solid',
                                borderColor: theme => theme.palette.mode === 'dark' ? '#2d2d44' : '#f0f0f0',
                                '&:last-child': { borderBottom: 'none' },
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: theme => theme.palette.mode === 'dark' ? '#252540' : '#f9f9f9',
                                },
                            }}>
                            <ListItemAvatar sx={{ minWidth: 48 }}>
                                <Avatar
                                    src={company.brandLogo?.url ?? '/default-logo.png'}
                                    alt={company.commercialName}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: '#f0f0f0',
                                        color: '#4239b0',
                                    }}>
                                    <i className="fi fi-br-building" style={{ fontSize: '18px' }} />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                                        {company.commercialName}
                                    </Typography>
                                }
                                secondary={
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                        {formatDate(company.createdDate)}
                                    </Typography>
                                }
                            />
                            <i className="fi fi-br-angle-small-right" style={{ opacity: 0.5, fontSize: '14px' }} />
                        </ListItem>
                    ))
                ) : (
                    <EmptyState subtitle="Aucune nouvelle entreprise" />
                )}
            </List>
        </Card>
    );
};
